import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useWizard } from '../../context/WizardContext';
import { useLanguage } from '../../context/LanguageContext';
import ScoringEngine from '../../services/scoringEngine';
import { WizardService } from '../../services/wizardService';
import PathwayRecommendations from './PathwayRecommendations';
import { TOTAL_WIZARD_STEPS } from '../../constants/wizardSteps';

/**
 * Package option type for Germany services
 */
const GERMANY_PACKAGES = {
  ESSENTIAL: 'essential',
  PREMIUM: 'premium'
};

/**
 * Blocked account funding options
 */
const FUNDING_OPTIONS = {
  SELF_FUNDED: 'self_funded',
  SPONSOR_LETTER: 'sponsor_letter'
};

/**
 * Review component for reviewing and submitting the wizard data
 * @param {Object} props - Component props
 * @param {number} props.stepNumber - Current step number in wizard flow
 * @param {string} props.title - Step title displayed to user
 * @param {string} props.description - Optional description text
 * @param {Function} props.onNext - Callback when user submits wizard
 * @param {Function} props.onPrevious - Callback when user goes to previous step
 * @param {boolean} props.canGoNext - Whether next button should be enabled
 * @param {boolean} props.canGoPrevious - Whether previous button should be enabled
 * @param {string} props.className - Additional CSS classes
 */
const Review = ({
  stepNumber = 9,
  totalSteps = TOTAL_WIZARD_STEPS,
  title = "Profile Review",
  description = "Review your complete profile before final submission. Make sure all information is accurate.",
  onNext,
  onPrevious,
  canGoNext = false,
  canGoPrevious = true,
  className = ''
}) => {
  const { state, actions } = useWizard();
  const { t } = useLanguage();
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Auto-scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, []);
  const pathwayRef = useRef(null); // Reference to capture selected data

  // Dynamic scoring state
  const [scoringResults, setScoringResults] = useState(null);
  const [loadingScores, setLoadingScores] = useState(false);
  const [scoringError, setScoringError] = useState(null);

  // Pathways from Supabase
  const [pathwaysData, setPathwaysData] = useState([]);
  const [loadingPathways, setLoadingPathways] = useState(false);
  const [pathwaysError, setPathwaysError] = useState(null);
  
  // Track if we've already loaded
  const hasLoadedRef = useRef(false);

  // Package and funding selections for Germany
  const selectedPackage = GERMANY_PACKAGES.PREMIUM;
  const fundingOption = FUNDING_OPTIONS.SELF_FUNDED;

  // Calculate pathway scores and fetch pathways on component mount (once)
  useEffect(() => {
    // Prevent double execution in strict mode or re-renders
    if (hasLoadedRef.current) {
      console.log('Review: Already loaded, skipping...');
      return;
    }
    hasLoadedRef.current = true;

    const loadData = async () => {
      console.log('Review: Starting to load data...');
      console.log('Review: Selected country:', state.countrySelection?.selectedCountry);
      
      // Get country code
      const countryCode = getCountryCodeForScoring(state.countrySelection?.selectedCountry);
      console.log('Review: Country code:', countryCode);

      // Start both loading states
      setLoadingScores(true);
      setLoadingPathways(true);
      setScoringError(null);
      setPathwaysError(null);

      // Fetch pathways from Supabase
      try {
        console.log('Review: Fetching pathways...');
        const pathways = await WizardService.getImmigrationPathways(countryCode);
        console.log('Review: Pathways received:', pathways?.length || 0);
        
        if (!pathways || pathways.length === 0) {
          console.warn('Review: No pathways returned for country:', countryCode);
          setPathwaysError('No immigration pathways found for the selected country. Please contact support.');
        }
        
        setPathwaysData(pathways || []);
      } catch (error) {
        console.error('Review: Error fetching pathways:', error);
        setPathwaysError(error.message || 'Failed to load immigration pathways. Please try again.');
        setPathwaysData([]);
      } finally {
        console.log('Review: Pathways loading complete');
        setLoadingPathways(false);
      }

      // Calculate pathway scores
      try {
        console.log('Review: Calculating scores...');
        
        // Convert wizard state to user profile format for scoring engine
        const userProfile = {
          personalInfo: state.personalInfo,
          education: state.education,
          experience: state.experience,
          languageSkills: state.languageSkills,
          financialInfo: state.financialInfo,
          countrySelection: state.countrySelection
        };

        const results = await ScoringEngine.calculateAllPathwayScores(
          userProfile,
          null, // CV analysis - not implemented yet
          countryCode
        );
        console.log('Review: Scores calculated:', results ? 'success' : 'null');
        setScoringResults(results);
        
        // Save to global context for submission
        if (actions && typeof actions.setScores === 'function') {
          actions.setScores(results);
        }
      } catch (error) {
        console.error('Review: Error calculating pathway scores:', error);
        setScoringError(error.message);
        // Set a default empty result so the component doesn't hang on loading
        setScoringResults({
          pathways: {},
          bestMatch: null,
          country: null,
          userProfile: null,
          timestamp: new Date().toISOString()
        });
      } finally {
        console.log('Review: Scores loading complete');
        setLoadingScores(false);
      }

      console.log('Review: All loading complete');
    };

    loadData();
  }, []); // Empty dependency array - only run once on mount

  // Helper function to convert wizard country selection to ISO country code
  const getCountryCodeForScoring = (selectedCountry) => {
    const countryMapping = {
      'germany': 'DEU',
      'canada': 'CAN',
      'australia': 'AUS',
      'uk': 'GBR',
      'united kingdom': 'GBR',
      'usa': 'USA',
      'united states': 'USA',
      'notsure': 'DEU', // Default to Germany when user is not sure
      'not sure': 'DEU'
    };
    return countryMapping[selectedCountry?.toLowerCase()] || 'DEU'; // Default to Germany
  };

  const handleNext = useCallback(() => {
    // Get selected pathway and package from PathwayRecommendations component
    const pathwayData = pathwayRef.current?.getSelectedData() || {};

    // Save the review data to context for submission step
    actions.setReviewData({
      selectedPathway: pathwayData.selectedPathway,
      selectedPackage: pathwayData.selectedPackage,
      reviewed: true,
      timestamp: new Date().toISOString(),
      fundingOption: fundingOption
    });

    if (onNext) {
      onNext({
        reviewed: true,
        timestamp: new Date().toISOString(),
        selectedPackage: selectedPackage,
        fundingOption: fundingOption
      });
    }
  }, [onNext, actions, selectedPackage, fundingOption]);

  // Calculate package costs
  const getPackageCost = () => {
    if (state.countrySelection.selectedCountry === 'germany') {
      return selectedPackage === GERMANY_PACKAGES.PREMIUM ? 1190000 : 850000;
    }
    return 0;
  };

  const getBlockedAccountCost = () => {
    if (fundingOption === FUNDING_OPTIONS.SPONSOR_LETTER) {
      return 0; // Sponsor covers this
    }
    return 5370800;
  };

  const formatData = useCallback((data, type) => {
    if (!data || typeof data !== 'object') return 'Not provided';

    switch (type) {
      case 'country':
        return data.selectedCountry || 'Not selected';

      case 'personal':
        return `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Not provided';

      case 'education':
        return `${data.highestEducation || 'Not specified'} in ${data.courseOfStudy || data.fieldOfStudy || 'Not specified'}`;

      case 'experience':
        if (Array.isArray(data) && data.length > 0) {
          const exp = data[0];
          return `${exp.totalExperience || 'Not specified'} as ${exp.jobTitle || 'Not specified'}`;
        }
        return 'Not provided';

      case 'language':
        if (data.englishSpeaking) {
          const proficiency = {
            'beginner': 'Beginner',
            'intermediate': 'Intermediate',
            'advanced': 'Advanced',
            'native': 'Native/Fluent'
          };
          return `${proficiency[data.englishSpeaking] || 'Not specified'} English proficiency`;
        }
        return 'Not provided';

      case 'financial':
        return `Income: ${data.annualIncome || 'Not specified'}, Budget: ${data.educationBudget || 'Not specified'}`;

      default:
        return JSON.stringify(data, null, 2);
    }
  }, []);

  const getRecommendationScore = useCallback(() => {
    // Simple scoring algorithm based on collected data
    let score = 0;

    // Country selection
    if (state.countrySelection.selectedCountry) score += 10;

    // Personal info completeness
    if (state.personalInfo.firstName && state.personalInfo.lastName) score += 10;
    if (state.personalInfo.email && state.personalInfo.phone) score += 10;

    // Education
    if (state.education.highestEducation) score += 15;
    if (state.education.fieldOfStudy) score += 5;
    if (state.education.courseOfStudy) score += 5;

    // Experience
    if (state.experience.length > 0 && state.experience[0].totalExperience) score += 15;
    if (state.experience.length > 0 && state.experience[0].jobTitle) score += 10;

    // Language skills
    if (state.languageSkills.englishSpeaking) score += 15;
    if (state.languageSkills.hasIELTS === 'yes') score += 10;

    // Financial info
    if (state.financialInfo.annualIncome) score += 10;
    if (state.financialInfo.educationBudget) score += 10;

    return Math.min(score, 100);
  }, [state]);

  const score = getRecommendationScore();
  const scoreColor = score >= 70 ? 'text-green-600' : score >= 50 ? 'text-yellow-600' : 'text-red-600';

  const getScoreMessage = useCallback(() => {
    if (score >= 70) return 'Excellent profile for immigration!';
    if (score >= 50) return 'Good profile with potential';
    return 'Profile needs improvement';
  }, [score]);

  const recommendedTitle = t('wizard.review.recommendations.title', 'Recommended Immigration Options');

  return (
    <div className={`review-step w-full mx-auto ${className}`}>
      {/* Header */}
      <div className="text-center mb-6 sm:mb-8">
        <div className="mb-2">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-blue-100 text-blue-800">
            {t('wizard.common.stepIndicator', 'Step {{current}} of {{total}}', {
              current: stepNumber,
              total: totalSteps
            })}
          </span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">{title}</h2>
        <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">{description}</p>
      </div>

      {/* Profile Score - Compact */}
      <div className="card p-4 sm:p-3 mb-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex-1">
            <h3 className="text-base sm:text-sm font-semibold text-gray-900 mb-1">Immigration Eligibility Score</h3>
            <p className={`text-sm sm:text-xs font-medium ${scoreColor}`}>{getScoreMessage()}</p>
          </div>
          <div className="flex items-center gap-3 sm:gap-2 self-end sm:self-auto">
            <div className="text-right">
              <div className={`text-2xl sm:text-xl font-bold ${scoreColor}`}>{score}</div>
              <div className="text-xs text-gray-600">/ 100</div>
            </div>
            <div className="w-14 h-14 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
              <svg className="w-7 h-7 sm:w-6 sm:h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards - Compact */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-2 mb-4">
        {/* Country Selection */}
        <div className="card p-3 sm:p-3">
          <div className="flex items-center mb-2 sm:mb-1">
            <div className="w-7 h-7 sm:w-6 sm:h-6 bg-blue-100 rounded flex items-center justify-center mr-2 flex-shrink-0">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-sm sm:text-xs font-semibold text-gray-900">Destination</h4>
          </div>
          <p className="text-sm sm:text-xs text-gray-700 line-clamp-1">{formatData(state.countrySelection, 'country')}</p>
        </div>

        {/* Personal Information */}
        <div className="card p-3 sm:p-3">
          <div className="flex items-center mb-2 sm:mb-1">
            <div className="w-7 h-7 sm:w-6 sm:h-6 bg-green-100 rounded flex items-center justify-center mr-2 flex-shrink-0">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h4 className="text-sm sm:text-xs font-semibold text-gray-900">Personal Info</h4>
          </div>
          <p className="text-sm sm:text-xs text-gray-700 line-clamp-1">{formatData(state.personalInfo, 'personal')}</p>
        </div>

        {/* Education */}
        <div className="card p-3 sm:p-3">
          <div className="flex items-center mb-2 sm:mb-1">
            <div className="w-7 h-7 sm:w-6 sm:h-6 bg-purple-100 rounded flex items-center justify-center mr-2 flex-shrink-0">
              <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
              </svg>
            </div>
            <h4 className="text-sm sm:text-xs font-semibold text-gray-900">Education</h4>
          </div>
          <p className="text-sm sm:text-xs text-gray-700 line-clamp-1">{formatData(state.education, 'education')}</p>
        </div>

        {/* Experience */}
        <div className="card p-3 sm:p-3">
          <div className="flex items-center mb-2 sm:mb-1">
            <div className="w-7 h-7 sm:w-6 sm:h-6 bg-orange-100 rounded flex items-center justify-center mr-2 flex-shrink-0">
              <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A8 8 0 0112 21a8 8 0 01-9-7.045M12 3a8 8 0 017.745 10.255M12 3v9m0-9l3 3m-3-3L9 6" />
              </svg>
            </div>
            <h4 className="text-sm sm:text-xs font-semibold text-gray-900">Experience</h4>
          </div>
          <p className="text-sm sm:text-xs text-gray-700 line-clamp-1">{formatData(state.experience, 'experience')}</p>
        </div>

        {/* Language Skills */}
        <div className="card p-3 sm:p-3">
          <div className="flex items-center mb-2 sm:mb-1">
            <div className="w-7 h-7 sm:w-6 sm:h-6 bg-teal-100 rounded flex items-center justify-center mr-2 flex-shrink-0">
              <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
            </div>
            <h4 className="text-sm sm:text-xs font-semibold text-gray-900">Language Skills</h4>
          </div>
          <p className="text-sm sm:text-xs text-gray-700 line-clamp-1">{formatData(state.languageSkills, 'language')}</p>
        </div>

        {/* Financial Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 sm:p-3">
          <div className="flex items-center mb-2 sm:mb-1">
            <div className="w-7 h-7 sm:w-6 sm:h-6 bg-yellow-100 rounded flex items-center justify-center mr-2 flex-shrink-0">
              <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-sm sm:text-xs font-semibold text-gray-900">Financial Info</h4>
          </div>
          <p className="text-sm sm:text-xs text-gray-700 line-clamp-1">{formatData(state.financialInfo, 'financial')}</p>
        </div>
      </div>

      {/* Matching Options Section - Using Supabase Data */}
      <div className="mb-6">
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">{recommendedTitle}</h3>
        <PathwayRecommendations
          ref={pathwayRef}
          pathwaysData={pathwaysData}
          scoringResults={scoringResults}
          loading={loadingPathways || loadingScores}
          error={pathwaysError || scoringError}
        />
      </div>

      {/* Terms and Conditions */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className="mt-1 flex-shrink-0"
          />
          <div className="text-sm text-gray-700">
            I agree to the Terms of Service and Privacy Policy. I confirm that all information provided is accurate and complete.
          </div>
        </label>
      </div>

      {/* Navigation */}
      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-0">
        <button
          onClick={onPrevious}
          disabled={!canGoPrevious}
          className="btn btn-outline order-2 sm:order-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('wizard.common.previous', 'Previous')}
        </button>

        <button
          onClick={handleNext}
          disabled={!agreedToTerms}
          className="btn btn-accent order-1 sm:order-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('wizard.review.proceedButton', 'Proceed to Final Step')}
        </button>
      </div>
    </div>
  );
};

export default Review;
